import { FormFieldProps } from '@App/forms/common/form-field'
import { DropdownFormFieldProps } from '@App/forms/common/dropdown-form-field'
import { ONLY_NUMBERS_REGEX } from '@Const/regexp'
import { IS_NUMBER_MESSAGE, MAX_LENGTH_MESSAGE, MIN_LENGTH_MESSAGE, REQUIRED_MESSAGE } from '@Const/error-messages'
import { FormFieldTypes } from '@App/enum/form-field-types'

export interface PhoneNumberFormDefinition {
    phoneCountryCode: string
    phoneAreaCode?: string
    phoneNumber: string
}

export interface PhoneNumberFormInput {
    phoneCountryCode: string | number
    phoneAreaCode?: string
    phoneNumber: string
}

export interface PhoneNumberFormProps {
    phoneCountryCode: DropdownFormFieldProps
    phoneAreaCode: FormFieldProps
    phoneNumber: FormFieldProps
}

const getDefaultPhoneCountryCode = (prefix: string, optional?: boolean): DropdownFormFieldProps => ({
    name: prefix + 'phoneCountryCode',
    label: 'Country',
    type: FormFieldTypes.DROPDOWN,
    options: [],
    className: 'phone-code-form-type border',
    showLabelOnSelect: false,
    isPhoneType: true,
    assistiveTextFunction: (required: boolean): string => (required ? 'Country Required' : ''),
    registerOptions: {
        required: optional ? false : REQUIRED_MESSAGE,
    },
    isClearable: true,
})

const getDefaultPhoneAreaCode = (prefix: string, optional?: boolean): FormFieldProps => ({
    name: prefix + 'phoneAreaCode',
    label: 'Area Code',
    type: FormFieldTypes.TEXT,
    assistiveTextFunction: (required: boolean): string => (required ? 'Area Required' : 'Area'),
    registerOptions: {
        required: optional ? false : REQUIRED_MESSAGE,
        pattern: {
            value: ONLY_NUMBERS_REGEX,
            message: IS_NUMBER_MESSAGE,
        },
        maxLength: {
            value: 10,
            message: `${MAX_LENGTH_MESSAGE} 10`,
        },
        minLength: {
            value: 1,
            message: `${MIN_LENGTH_MESSAGE} 1`,
        },
    },
})

const getDefaultPhoneNumber = (prefix: string, optional?: boolean): FormFieldProps => ({
    name: prefix + 'phoneNumber',
    label: 'Phone Number',
    assistiveTextFunction: (required: boolean): string => (required ? 'Number Required' : 'Number'),
    type: FormFieldTypes.TEXT,
    registerOptions: {
        required: optional ? false : REQUIRED_MESSAGE,
        pattern: {
            value: ONLY_NUMBERS_REGEX,
            message: IS_NUMBER_MESSAGE,
        },
        maxLength: {
            value: 40,
            message: `${MAX_LENGTH_MESSAGE} 40`,
        },
        minLength: {
            value: 6,
            message: `${MIN_LENGTH_MESSAGE} 6`,
        },
    },
})

export const createPhoneNumberFormFields = (prefix = '', optional?: boolean): PhoneNumberFormProps => ({
    phoneCountryCode: getDefaultPhoneCountryCode(prefix, !!optional),
    phoneAreaCode: getDefaultPhoneAreaCode(prefix, !!optional),
    phoneNumber: getDefaultPhoneNumber(prefix, !!optional),
})

export const markFieldAsRequired = (
    phoneNumberFormProps: PhoneNumberFormProps,
    isOptional: boolean
): PhoneNumberFormProps => {
    phoneNumberFormProps.phoneCountryCode = {
        ...phoneNumberFormProps.phoneCountryCode,
        registerOptions: {
            ...phoneNumberFormProps.phoneCountryCode.registerOptions,
            required: isOptional ? false : REQUIRED_MESSAGE,
        },
    }
    phoneNumberFormProps.phoneNumber = {
        ...phoneNumberFormProps.phoneNumber,
        registerOptions: {
            ...phoneNumberFormProps.phoneNumber.registerOptions,
            required: isOptional ? false : REQUIRED_MESSAGE,
        },
    }

    return phoneNumberFormProps
}
